import { generateUID } from "@/canv/utils";

type SnackbarType = "info" | "loading" | "success" | "warning" | "error";
type SnackbarOptions = {
    type: SnackbarType;
    duration: number;
    onClose?: () => void;
    id?: string;
};

class SnackbarManager {
    constructor() {
        const container = document.createElement("div");
        container.classList.add("snackbars");
        this.container = container;
        document.body.appendChild(container);
    }

    show(
        message: string,
        options: SnackbarOptions = {
            type: "info",
            duration: 5000,
        },
    ) {
        if (options.id && this.snackbars.has(options.id)) {
            return this.update(options.id, {
                message,
                options,
            });
        }
        const id = options.id ?? generateUID();
        const snackbar = new Snackbar(message, {
            ...options,
            id,
            onClose: () => {
                options?.onClose?.();
                this.snackbars.delete(id);
            },
        });
        this.snackbars.set(id, snackbar);
        this.container.appendChild(snackbar.element);

        return snackbar;
    }

    update = (
        id: string,
        {
            message,
            options,
        }: {
            message?: string;
            options?: SnackbarOptions;
        },
    ) => {
        if (!this.snackbars.has(id)) {
            console.log(`no snackbar with id: ${id}`);
            throw Error(`no snackbar with id: ${id}`);
        }
        const snackbar = this.snackbars.get(id) as Snackbar;
        if (message && snackbar.message !== message) {
            // update message
            snackbar.updateText(message);
        }
        if (options) {
            if (snackbar.options.duration !== options.duration) {
                // update timeout
                snackbar.options.duration = options.duration;
            }
            if (snackbar.options.type !== options.type) {
                // TODO: change icon on fly
            }
        }
        snackbar.updateTimeout();
        return snackbar;
    };

    container: HTMLDivElement;
    snackbars = new Map<string, Snackbar>();
}

class Snackbar {
    constructor(message: string, options: SnackbarOptions) {
        this.message = message;
        this.options = { ...this.defaultOptions, ...options };
        this.element = this.createTemplate();
        this.timeoutId = null;

        if (!this.options.id) {
            this.options.id = generateUID();
        }

        if (this.options.duration > 0) {
            this.timeoutId = setTimeout(this.close, this.options.duration);
        }
    }

    options: SnackbarOptions;
    message: string;
    element: HTMLDivElement;
    timeoutId: NodeJS.Timeout | null;
    defaultOptions: SnackbarOptions = {
        type: "info",
        duration: 0,
        onClose: () => {},
    };

    createTemplate = () => {
        const element = document.createElement("div");
        element.classList.add("snackbar");

        const textElement = document.createElement("div");
        textElement.classList.add("snackbar__content");
        textElement.textContent = this.message;

        const closeElement = document.createElement("button");
        closeElement.setAttribute("type", "button");
        closeElement.setAttribute("aria-label", "Закрыть");
        closeElement.innerHTML = closeIcon;
        closeElement.classList.add("snackbar__close");
        closeElement.addEventListener("click", this.close);

        const type = this.options.type;

        if (type) {
            const iconElement = document.createElement("div");
            iconElement.classList.add("snackbar__icon");
            iconElement.classList.add(`snackbar__icon--${type}`);

            switch (type) {
                case "loading":
                    iconElement.innerHTML = loaderIcon;
                    break;
                case "success":
                    iconElement.innerHTML = successIcon;
                    break;
                case "warning":
                    iconElement.innerHTML = warningIcon;
                    break;
                case "error":
                    iconElement.innerHTML = errorIcon;
                    break;
                case "info":
                    iconElement.innerHTML = infoIcon;
                    break;
                default:
                    iconElement.innerHTML = infoIcon;
            }

            element.prepend(iconElement);
        }

        element.appendChild(textElement);
        element.appendChild(closeElement);

        return element;
    };

    updateText = (newText: string) => {
        const textElement = this.element.querySelector(".snackbar__content");
        if (textElement) {
            textElement.textContent = newText;
        }
        return this;
    };

    /**
     * reset timeout and start new one
     */
    updateTimeout = () => {
        if (this.timeoutId && this.options.duration > 0) {
            clearTimeout(this.timeoutId);
            this.timeoutId = setTimeout(this.close, this.options.duration);
        }
    };

    close = () => {
        this.element.remove();
        this.options.onClose?.();
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    };
}

const manager = new SnackbarManager();

type ShowSnackbarOptions = Partial<Pick<SnackbarOptions, "duration" | "id" | "type">>;
export const showSnackbar = (
    message: string,
    { duration = 0, type = "warning", id }: ShowSnackbarOptions = {},
) => {
    return manager.show(message, {
        duration,
        type,
        id,
    });
};

const closeIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path d="m16 4-12 12m0-12 12 12" stroke="#fafafa" stroke-linecap="round" stroke-linejoin="round"
        stroke-width="1.5" />
</svg>`;

const warningIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
        d="m10 6.4v3.6m0 3.6h.009m8.991-3.6c0-4.97056-4.0294-9-9-9-4.97056 0-9 4.02944-9 9 0 4.9706 4.02944 9 9 9 4.9706 0 9-4.0294 9-9z"
        stroke="#fad67d" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
</svg>`;

const successIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path d="m16.6667 5.41669-9.16666 9.16671-4.16667-4.1667" stroke="#74df8b" stroke-linecap="round"
        stroke-linejoin="round" stroke-width="1.5" />
</svg>`;

const loaderIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
        d="m18 10.0004c-.0001 1.6894-.535 3.3354-1.5281 4.7021-.993 1.3668-2.3933 2.384-4 2.906-1.6068.522-3.3375.522-4.94422-.0001-1.60671-.5221-3.0069-1.5394-3.99989-2.9062s-1.5278-3.0128-1.52779-4.70226c.00001-1.68941.53485-3.33545 1.52786-4.70221.99301-1.36677 2.39321-2.38409 3.99993-2.90616s3.33751-.52209 4.94421-.00007"
        stroke="#fafafa" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
</svg>`;

const infoIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
        d="m10 13.6v-3.6m0-3.6h.009m8.991 3.6c0 4.9706-4.0294 9-9 9-4.97056 0-9-4.0294-9-9 0-4.97056 4.02944-9 9-9 4.9706 0 9 4.02944 9 9z"
        stroke="#45b6fc" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" />
</svg>`;

const errorIcon = `
<svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
    <path
        d="m9.65039 0c5.32941.00021114 9.65041 4.32097 9.65041 9.65039-.0002 5.32921-4.3212 9.65021-9.65041 9.65041-5.32942 0-9.65017885-4.321-9.65039-9.65041 0-5.32955 4.32084-9.65039 9.65039-9.65039zm0 1.30078c-4.61158 0-8.34961 3.73803-8.34961 8.34961.00021 4.61141 3.73816 8.34961 8.34961 8.34961 4.61131-.0002 8.34941-3.7383 8.34961-8.34961 0-4.61145-3.7382-8.3494-8.34961-8.34961zm2.49221 5.15625c.2523-.16644.5953-.13905.8174.08301.222.22205.2494.5651.083.81738l-.083.10254-2.2901 2.29004 2.2901 2.29.083.1026c.1664.2523.139.5953-.083.8174-.2221.222-.5651.2494-.8174.083l-.1026-.083-2.29-2.2901-2.29004 2.2901c-.25384.2538-.66608.2538-.91992 0-.25384-.2539-.25384-.6661 0-.92l2.29004-2.29-2.29004-2.29004-.08301-.10254c-.16644-.25228-.13905-.59533.08301-.81738.22205-.22206.5651-.24945.81738-.08301l.10254.08301 2.29004 2.29004 2.29-2.29004z"
        fill="#fa8a64" />
</svg>`;
